import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'

interface LatestThreadsProps {
  userId: string
  /** When true, section starts expanded and fetches threads (e.g. on Home page). */
  defaultExpanded?: boolean
}

interface Thread {
  id: string
  title: string
  body: string
  created_at: string
  group_id: string
  groups: {
    id: string
    name: string
  }
  comment_count: number
  latest_comment_date: string | null
}

export default function LatestThreads({ userId, defaultExpanded = false }: LatestThreadsProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const { data: threads, isLoading } = useQuery({
    queryKey: ['latest-threads', userId],
    queryFn: async () => {
      try {
        // Get user's group IDs to exclude them
        const { data: memberships, error: membershipsError } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', userId)

        if (membershipsError) {
          console.warn('Error fetching group memberships:', membershipsError)
        }

        const userGroupIds = memberships?.map((m) => m.group_id) || []

        // Get threads from groups user hasn't joined
        const { data: threadsData, error } = await supabase
          .from('threads')
          .select(`
            id,
            title,
            body,
            created_at,
            group_id,
            groups (
              id,
              name
            )
          `)
          .eq('is_hidden', false)
          .order('created_at', { ascending: false })
          .limit(20) // Get more to filter out user's groups

        if (error) {
          console.warn('Error fetching threads:', error)
          return []
        }

        // Filter out threads from user's groups
        const filteredThreads = (threadsData || []).filter(
          (thread) => !userGroupIds.includes(thread.group_id)
        ).slice(0, 10) // Limit to 10 after filtering

        // Get comment counts and latest comment dates, transform data
        const threadsWithComments = await Promise.all(
          filteredThreads.map(async (thread: any) => {
            try {
              const { count } = await supabase
                .from('comments')
                .select('*', { count: 'exact', head: true })
                .eq('thread_id', thread.id)
                .eq('is_hidden', false)

              const { data: latestComment } = await supabase
                .from('comments')
                .select('created_at')
                .eq('thread_id', thread.id)
                .eq('is_hidden', false)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle()

              // Transform data: Supabase returns groups as array, extract first element
              return {
                ...thread,
                groups: Array.isArray(thread.groups)
                  ? (thread.groups[0] || null)
                  : thread.groups,
                comment_count: count || 0,
                latest_comment_date: latestComment?.created_at || null,
              }
            } catch (err) {
              console.warn('Error fetching comment data:', err)
              return {
                ...thread,
                groups: Array.isArray(thread.groups)
                  ? (thread.groups[0] || null)
                  : thread.groups,
                comment_count: 0,
                latest_comment_date: null,
              }
            }
          })
        )

        return threadsWithComments as Thread[]
      } catch (err) {
        console.warn('Error in latest threads query:', err)
        return []
      }
    },
    enabled: isExpanded,
    retry: false,
  })

  if (!isExpanded) {
    return (
      <div 
        className="rounded-lg p-6 border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 
              className="text-xl font-bold mb-1"
              style={{ color: 'var(--text-primary)' }}
            >
              Latest Threads
            </h2>
            <p 
              className="text-sm"
              style={{ color: 'var(--text-primary)' }}
            >
              Recent activity from groups you haven't joined yet
            </p>
          </div>
          <button
            onClick={() => setIsExpanded(true)}
            className="px-4 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Expand
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div 
        className="rounded-lg p-6 border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
        }}
      >
        <p style={{ color: 'var(--text-primary)' }}>Loading threads...</p>
      </div>
    )
  }

  return (
    <div 
      className="rounded-lg p-6 border-2"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--bg-tertiary)',
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h2 
            className="text-xl font-bold mb-1"
            style={{ color: 'var(--text-primary)' }}
          >
            Latest Threads
          </h2>
          <p 
            className="text-sm"
            style={{ color: 'var(--text-primary)' }}
          >
            Recent activity from groups you haven't joined yet
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/activity"
            className="px-4 py-2 font-semibold rounded underline"
            style={{ color: 'var(--text-link)' }}
          >
            View all
          </Link>
          <button
            onClick={() => setIsExpanded(false)}
            className="px-4 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Collapse
          </button>
        </div>
      </div>

      {!threads || threads.length === 0 ? (
        <p style={{ color: 'var(--text-primary)' }}>
          No threads available.
        </p>
      ) : (
        <div className="space-y-4">
          {threads.map((thread) => {
            const group = thread.groups
            if (!group) return null

            const latestCommentDate = thread.latest_comment_date
              ? format(new Date(thread.latest_comment_date), 'dd/MM/yyyy, HH:mm:ss')
              : null

            return (
              <div
                key={thread.id}
                className="p-4 rounded border-2"
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  borderColor: 'var(--bg-tertiary)',
                }}
              >
                <h3 
                  className="text-lg font-bold mb-2"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {thread.title}
                </h3>
                <Link
                  to={`/groups/${group.id}`}
                  className="text-sm mb-2 block"
                  style={{ color: 'var(--text-link)' }}
                >
                  {group.name}
                </Link>
                <p 
                  className="mb-3"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {thread.body.length > 200 
                    ? `${thread.body.substring(0, 200)}...` 
                    : thread.body}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <span>{thread.comment_count} comments</span>
                    {latestCommentDate && (
                      <span className="ml-4">
                        Latest comment: {latestCommentDate}
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/groups/${group.id}`}
                    state={{ selectedThreadId: thread.id }}
                    className="px-4 py-2 font-semibold rounded"
                    style={{
                      backgroundColor: 'var(--bg-interactive)',
                      color: 'var(--text-inverse)',
                    }}
                  >
                    View Thread
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

